/**
 * Converts a time string to milliseconds
 * Supports: s, sec, second, seconds, m, min, minute, minutes, h, hour, hours, d, day, days
 * @param time - Time string in format "<number><unit>" (e.g., "1h", "30m", "2d")
 * @returns Number of milliseconds
 * @throws Error if input is invalid
 */
export function ms(time: string): number {
  if (typeof time !== 'string') {
    throw new Error('Time must be a string');
  }

  const trimmed = time.trim();
  if (!trimmed) {
    throw new Error('Time string cannot be empty');
  }

  // Match pattern: number followed by unit (with optional whitespace)
  const match = trimmed.match(/^(\d+)\s*([a-zA-Z]+)$/);
  if (!match) {
    throw new Error(
      `Invalid time format: "${time}". Expected format: "<number><unit>"`,
    );
  }

  const [, numberStr, unit] = match;
  const number = parseInt(numberStr, 10);

  if (isNaN(number) || number < 0) {
    throw new Error(`Invalid number: "${numberStr}"`);
  }

  const values = {
    sc: 1000,
    mn: 60 * 1000,
    hr: 60 * 60 * 1000,
    dy: 24 * 60 * 60 * 1000,
    mo: 30 * 24 * 60 * 60 * 1000,
    yr: 365 * 24 * 60 * 60 * 1000,
  };

  // Convert unit to milliseconds
  const unitMap: Record<string, number> = {
    // Seconds
    s: values.sc,
    sec: values.sc,
    second: values.sc,
    seconds: values.sc,
    // Minutes
    m: values.mn,
    mn: values.mn,
    min: values.mn,
    minute: values.mn,
    minutes: values.mn,
    // Hours
    h: values.hr,
    hr: values.hr,
    hour: values.hr,
    hours: values.hr,
    // Days
    d: values.dy,
    dy: values.dy,
    day: values.dy,
    days: values.dy,
    // Months
    mo: values.mo,
    mth: values.mo,
    mths: values.mo,
    month: values.mo,
    months: values.mo,
    // Years
    y: values.yr,
    yr: values.yr,
    yrs: values.yr,
    year: values.yr,
    years: values.yr,
  };

  const multiplier = unitMap[unit.toLowerCase()];
  if (!multiplier) {
    throw new Error(
      `Invalid unit: "${unit}". Supported units: s, sec, second, seconds, m, min, minute, minutes, h, hour, hours, d, day, days`,
    );
  }

  return number * multiplier;
}
